import {
    lookupSvc,
    staticReportsSnowflakeSvc as reportSvc,
    scenarioSvc,
    snowflakeTablesService as snowTablesSvc,
    snowflakeLoadConfigService as snowConfigSvc,
    snowflakeLoadService as snowLoadSvc,
} from "services/serviceStorage";
import { BaseApiResponse, ReportingLookup } from "common/types";
import { taskEngines } from "common/enums";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "store";

import {
    GetTemplatesParams,
    GetStaticReportsParams,
    FetchStaticReportsParams,
    ReportHistoryItem,
    Scenario,
    Template,
    StaticReport,
    SnowflakeTable,
    SnowflakeTablesFilter,
    LoadConfig,
    SnowflakeLoadConfigFilter,
    SnowflakeLoadStatusesFilter,
    SnowflakeLoadStatus
} from "./types";

export const getStaticReportHistories = createAsyncThunk<{ items: ReportHistoryItem[]; totalCount: number; }, void, { state: RootState }>(
    'staticReportsSnowflake/getStaticReportHistories',
    async (_, { getState }) => {
        const { snowflakeHistories, lookup } = getState().staticReportsSnowflakeSlice;

        const { data } = await wrapRequest<BaseApiResponse<{ items: ReportHistoryItem[]; totalCount: number; }>>({
            handler: () => reportSvc.getStaticReportHistories({ ...snowflakeHistories.filter, taskId: lookup.taskId }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getScenarios = createAsyncThunk<{ items: Scenario[]; totalCount: number; }, void, { state: RootState }>(
    'staticReportsSnowflake/getScenarios',
    async (_, { getState }) => {
        const { scenarios, lookup } = getState().staticReportsSnowflakeSlice;

        const { data } = await wrapRequest<BaseApiResponse<{ items: Scenario[]; totalCount: number; }>>({
            handler: () => reportSvc.getScenarios({ ...scenarios.filter, searchString: scenarios.filter.searchString?.trim(), taskId: lookup.taskId }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getCaseScenarios = createAsyncThunk<{ items: Scenario[]; totalCount: number; }, void, { state: RootState }>(
    'staticReportsSnowflake/getCaseScenarios',
    async (_, { getState }) => {
        const { scenarios, lookup } = getState().staticReportsSnowflakeSlice;

        const { data } = await wrapRequest<BaseApiResponse<{ items: Scenario[]; totalCount: number; }>>({
            handler: () => scenarioSvc.getScenarios({
                ...scenarios.filter,
                searchString: scenarios.filter.searchString?.trim(),
                taskId: lookup.taskId,
                hasInputs: lookup.engine !== taskEngines.enCompass
            }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getTemplates = createAsyncThunk<{ items: Template[]; totalCount: number; }, GetTemplatesParams>(
    'staticReportsSnowflake/getTemplates',
    async ({ taskIds, releaseGroupIds }) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Template[]; totalCount: number; }>>({
            handler: () => reportSvc.getTemplates({ taskIds, releaseGroupIds }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getTemplate = createAsyncThunk<Template, number>(
    'staticReportsSnowflake/getTemplate',
    async (templateId) => {
        const { data } = await wrapRequest<BaseApiResponse<Template>>({
            handler: () => reportSvc.getTemplate(templateId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const fetchReportingLookup = createAsyncThunk<ReportingLookup, number>(
    'staticReportsSnowflake/fetchReportingLookup',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<ReportingLookup>>({
            handler: () => lookupSvc.getReportingLookup(taskId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getStaticReports = createAsyncThunk<{ items: StaticReport[]; totalCount: number; }, GetStaticReportsParams>(
    'staticReportsSnowflake/getStaticReports',
    async (filters) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: StaticReport[]; totalCount: number; }>>({
            handler: () => reportSvc.getStaticReports(filters),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getStaticReport = createAsyncThunk<StaticReport, number>(
    'staticReportsSnowflake/getStaticReport',
    async (reportId) => {
        const { data } = await wrapRequest<BaseApiResponse<StaticReport>>({
            handler: () => reportSvc.getStaticReport(reportId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const deleteTemplates = createAsyncThunk<void, number[]>(
    'staticReportsSnowflake/deleteTemplates',
    async (templateIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.deleteTemplates(templateIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const deleteStaticReports = createAsyncThunk<void, number[]>(
    'staticReportsSnowflake/deleteStaticReports',
    async (reportIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.deleteStaticReports(reportIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchRunReports = createAsyncThunk<void, FetchStaticReportsParams>(
    'staticReportsSnowflake/fetchRunReports',
    async (request, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.runReportsForScenarios(request),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const saveStaticReport = createAsyncThunk<void, Partial<StaticReport>>(
    'staticReportsSnowflake/saveStaticReport',
    async (report, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.saveStaticReport(report),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const saveTemplate = createAsyncThunk<Template, Partial<Template>>(
    'staticReportsSnowflake/saveTemplate',
    async (template, { rejectWithValue }) => {
        const { data, errors, warnings } = await wrapRequest<BaseApiResponse<Template>>({
            handler: () => reportSvc.saveTemplate(template),
            showNotifications: true
        });

        if (errors?.length || warnings?.length) {
            return rejectWithValue({ errors, warnings });
        }

        return data?.result!;
    }
);

export const downloadResults = createAsyncThunk<{ [key: string]: string }, string[]>(
    'staticReportsSnowflake/downloadResults',
    async (queryExecutionIds) => {
        const { data } = await wrapRequest<BaseApiResponse<{ [key: string]: string }>>({
            handler: () => reportSvc.downloadResults(queryExecutionIds),
            showNotifications: true
        });

        return data!.result;
    }
);

export const cancelExecutions = createAsyncThunk<void, number[]>(
    'staticReportsSnowflake/cancelExecutions',
    async (reportHistoryIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.cancelExecutions(reportHistoryIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const getTables = createAsyncThunk<{ items: SnowflakeTable[]; totalCount: number }, SnowflakeTablesFilter>(
    'staticReportsSnowflake/getTables',
    async (filters) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: SnowflakeTable[]; totalCount: number }>>({
            handler: () => snowTablesSvc.list(filters),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getLoadConfig = createAsyncThunk<LoadConfig, number>(
    'staticReportsSnowflake/getLoadConfig',
    async (confiId) => {
        const { data } = await wrapRequest<BaseApiResponse<LoadConfig>>({
            handler: () => snowConfigSvc.getConfig(confiId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getLoadConfigs = createAsyncThunk<{ items: LoadConfig[]; totalCount: number }, SnowflakeLoadConfigFilter>(
    'staticReportsSnowflake/getLoadConfigs',
    async (filters) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: LoadConfig[]; totalCount: number }>>({
            handler: () => snowConfigSvc.list(filters),
            showNotifications: true
        });

        return data!.result;
    }
);

export const createLoadConfig = createAsyncThunk<number, { configId: number | null; configName: string; tables: number[]; taskId: number | null; releaseGroupId: number; }>(
    'staticReportsSnowflake/createLoadConfig',
    async (config, { rejectWithValue }) => {
        const { data, ...err } = await wrapRequest<BaseApiResponse<number>>({
            handler: () => snowConfigSvc.createConfig({
                id: config.configId,
                name: config.configName,
                releaseGroupId: config.releaseGroupId,
                taskId: config.taskId,
                tableIds: config.tables
            }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }

        return data!.result;
    }
);

export const deleteLoadConfigs = createAsyncThunk<void, number[]>(
    'staticReportsSnowflake/deleteLoadConfigs',
    async (configIds) => {
        const { data } = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowConfigSvc.deleteConfigs(configIds),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getAvailableTables = createAsyncThunk<SnowflakeTable[], number>(
    'staticReportsSnowflake/getAvailableTables',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<SnowflakeTable[]>>({
            handler: () => snowTablesSvc.getAvailableTables(scenarioId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getLoadStatuses = createAsyncThunk<SnowflakeLoadStatus[], Partial<SnowflakeLoadStatusesFilter>>(
    'staticReportsSnowflake/getLoadStatuses',
    async (filters) => {
        const { data } = await wrapRequest<BaseApiResponse<SnowflakeLoadStatus[]>>({
            handler: () => snowLoadSvc.getLoadStatuses(filters),
            showNotifications: true
        });

        return data!.result;
    }
);

export const loadTables = createAsyncThunk<void, { scenarioId: number; tableId: number }[]>(
    'staticReportsSnowflake/loadTables',
    async (request, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowLoadSvc.loadTables(request),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const setReportsDependencies = createAsyncThunk<void, number>(
    'staticReportsSnowflake/setReportsDependencies',
    async (releaseGroupId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => reportSvc.setReportsDependencies(releaseGroupId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);